/* <product-description-more> — clamps a product description and reveals the rest
   behind a "+ Read more" toggle.

   Paired with snippets/product-description-more.liquid and
   assets/product-description-more.css.

   Progressive enhancement: the snippet renders the full description with the
   toggle hidden. This element adds the clamp only after measuring that the text
   really is taller than the collapsed height, so short descriptions never show a
   button that does nothing.

   Not a ModalElement/DrawerElement case (nothing overlays), and not
   AccordionDetails either — <details> hides every non-<summary> child when
   closed, so it cannot show the first few lines as a preview. Hence a small
   element of its own, following the theme's custom-element convention. */

if (!customElements.get('product-description-more')) {
  class ProductDescriptionMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.pdm__content');
      this.toggle = this.querySelector('.pdm__toggle');
      if (!this.content || !this.toggle) return;

      this.labelEl = this.toggle.querySelector('.pdm__label');
      this.moreLabel = this.dataset.more || 'Read more';
      this.lessLabel = this.dataset.less || 'Read less';
      this.open = false;

      this.onToggle = this.onToggle.bind(this);
      this.onResize = this.onResize.bind(this);
      this.toggle.addEventListener('click', this.onToggle);

      // Fonts and lazily-loaded images shift the height; measure once settled.
      this.measure();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure()).catch(() => {});
      }
      window.addEventListener('resize', this.onResize);
      this.classList.add('pdm--ready');
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      clearTimeout(this._resizeTimer);
    }

    /* The toggle continues the text rather than sitting on its own line.
       Collapsed, CSS parks it at the end of the last visible line (it must stay a
       child of this element, outside .pdm__content's overflow:hidden, or it would
       be clipped). Expanded, there is nothing to clip, so it moves inside the last
       block of the description and simply flows after the final sentence. */
    placeToggle() {
      if (this.open) {
        const last = this.lastTextBlock();
        if (last && this.toggle.parentElement !== last) last.appendChild(this.toggle);
      } else if (this.toggle.parentElement !== this) {
        this.appendChild(this.toggle);
      }
    }

    lastTextBlock() {
      const blocks = this.content.querySelectorAll('p, li, h2, h3, h4, blockquote');
      for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].textContent.trim()) return blocks[i];
      }
      return this.content;
    }

    measure() {
      // Measure unclamped so scrollHeight is the true content height.
      const wasClamped = this.classList.contains('pdm--clamped');
      if (wasClamped && !this.open) this.classList.remove('pdm--clamped');
      const full = this.content.scrollHeight;
      if (wasClamped && !this.open) this.classList.add('pdm--clamped');

      const lines = parseInt(this.dataset.lines, 10) || 4;
      const lh = parseFloat(getComputedStyle(this.content).lineHeight) || 24;
      const collapsed = lines * lh;
      this.style.setProperty('--pdm-collapsed', `${collapsed}px`);
      this.style.setProperty('--pdm-expanded', `${full}px`);

      // A couple of px of slack: don't add a toggle for a sliver of overflow.
      const overflows = full > collapsed + 4;
      this.classList.toggle('pdm--clamped', overflows);
      this.toggle.hidden = !overflows;
      if (!overflows) {
        this.open = false;
        this.classList.remove('pdm--open');
        this.toggle.setAttribute('aria-expanded', 'false');
        if (this.labelEl) this.labelEl.textContent = this.moreLabel;
        this.placeToggle(); // back out of the description, ready to be re-parked
      }
    }

    onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.measure(), 150);
    }

    onToggle(event) {
      event.preventDefault();
      this.open = !this.open;
      this.classList.toggle('pdm--open', this.open);
      this.toggle.setAttribute('aria-expanded', this.open ? 'true' : 'false');
      if (this.labelEl) this.labelEl.textContent = this.open ? this.lessLabel : this.moreLabel;

      this.placeToggle();

      if (this.open) {
        // Measure AFTER placeToggle: expanded, the toggle lives inside the
        // content, so it counts towards the height we animate to.
        this.style.setProperty('--pdm-expanded', `${this.content.scrollHeight}px`);
      } else {
        // Collapsing from off-screen content is disorienting; keep the toggle in view.
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  customElements.define('product-description-more', ProductDescriptionMore);
}
